import axios from 'axios';
import type { 
  Program, 
  Submission, 
  Receipt,
  CreateSubmissionRequest,
  BulkCreateSubmissionsRequest,
  BulkCreateResult,
  UserManagement,
} from '../types';
import { msalInstance } from '../index';
import { loginRequest } from '../authConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5279/api';

const API = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Track if we're already refreshing to prevent loops
let isRefreshing = false;

// Request interceptor - Add access token to every request
API.interceptors.request.use(
  async (config) => {
    // Don't add token if we're already refreshing
    if (isRefreshing) {
      return config;
    }

    try {
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length > 0) {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        
        config.headers.Authorization = `Bearer ${response.accessToken}`;
        console.log('✅ Token attached to request');
      } else {
        console.warn('⚠️ No accounts found');
      }
    } catch (error) {
      console.error('❌ Error acquiring token:', error);
      // Don't throw - let request continue, API will return 401
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors WITHOUT infinite loop
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshing) {
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('🔄 Token expired, attempting silent refresh...');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh: true,
          });
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
          isRefreshing = false;
          
          console.log('✅ Token refreshed successfully');
          return API(originalRequest);
        }
      } catch (refreshError) {
        isRefreshing = false;
        console.error('❌ Token refresh failed:', refreshError);
        
        // Redirect to login page (within the app)
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Programs API
export const programsAPI = {
  // Employee endpoints (old names for compatibility)
  getActivePrograms: () => API.get('/programs/active').then(res => res.data),
  getGeneralProgram: () => API.get('/programs/general').then(res => res.data),
  getProgram: (id: string) => API.get(`/programs/${id}`).then(res => res.data),
  
  // Also support new names
  getActive: () => API.get('/programs/active').then(res => res.data),
  getGeneral: () => API.get('/programs/general').then(res => res.data),
  getById: (id: string) => API.get(`/programs/${id}`).then(res => res.data),
  
  // Admin endpoints
  getAllPrograms: (includeArchived = false) => 
    API.get(`/programs/admin/all?includeArchived=${includeArchived}`).then(res => res.data),
  getProgramAdmin: (id: string) => API.get(`/programs/admin/${id}`).then(res => res.data),
  createProgram: (data: any) => API.post('/programs', data).then(res => res.data),
  updateProgram: (id: string, data: any) => API.put(`/programs/${id}`, data).then(res => res.data),
  archiveProgram: (id: string, reason?: string) => 
    API.post(`/programs/${id}/archive`, { reason }).then(res => res.data),
  unarchiveProgram: (id: string) => API.post(`/programs/${id}/unarchive`).then(res => res.data),
  deleteProgram: (id: string) => API.delete(`/programs/${id}`).then(res => res.data),
  
  // Also support short names
  create: (data: any) => API.post('/programs', data).then(res => res.data),
  update: (id: string, data: any) => API.put(`/programs/${id}`, data).then(res => res.data),
  delete: (id: string) => API.delete(`/programs/${id}`).then(res => res.data),
};

// Submissions API
export const submissionsAPI = {
  // Old names for compatibility
  createSubmission: (data: any) => API.post('/submissions', data).then(res => res.data),
  getSubmission: (id: string) => API.get(`/submissions/${id}`).then(res => res.data),
  getMySubmissions: () => API.get('/submissions/my-submissions').then(res => res.data.submissions),
  
  // Bulk create
  bulkCreate: async (request: BulkCreateSubmissionsRequest): Promise<BulkCreateResult> => {
    const response = await API.post<BulkCreateResult>('/submissions/bulk-create', request);
    return response.data;
  },
  
  // Review endpoints (old names)
  getPendingManagerReview: () => API.get('/submissions/pending-review').then(res => res.data.submissions),
  getPendingAdminReview: () => API.get('/submissions/pending-admin-review').then(res => res.data.submissions),
  getAllForManager: () => API.get('/submissions/manager/all').then(res => res.data.submissions),
  getAllForAdmin: () => API.get('/submissions/admin/all').then(res => res.data.submissions),
  
  submitForApproval: (id: string, signature: string) => 
    API.put(`/submissions/${id}/submit`, { signature }).then(res => res.data),
  deleteSubmission: (id: string) => API.delete(`/submissions/${id}`),
  
  managerReview: (id: string, approved: boolean, signature: string, notes?: string) =>
    API.put(`/submissions/${id}/review`, { approved, signature, notes }).then(res => res.data),
  adminReview: (id: string, approved: boolean, signature: string, notes?: string) =>
    API.put(`/submissions/${id}/admin-review`, { approved, signature, notes }).then(res => res.data),
  
  // Also support new names
  create: (data: any) => API.post('/submissions', data).then(res => res.data),
  getById: (id: string) => API.get(`/submissions/${id}`).then(res => res.data),
  submit: (id: string, signature: string) => 
    API.put(`/submissions/${id}/submit`, { signature }).then(res => res.data),
  delete: (id: string) => API.delete(`/submissions/${id}`),
  review: (id: string, approved: boolean, signature: string, notes?: string) =>
    API.put(`/submissions/${id}/review`, { approved, signature, notes }).then(res => res.data),
};

// Receipts API
export const receiptsAPI = {
  // Old names for compatibility
  getReceipts: (submissionId: string) => 
    API.get(`/submissions/${submissionId}/receipts`).then(res => res.data),
  
  addReceipt: (submissionId: string, data: any) =>
    API.post(`/submissions/${submissionId}/receipts`, data).then(res => res.data),
  
  updateReceipt: (submissionId: string, receiptId: string, data: any) =>
    API.put(`/submissions/${submissionId}/receipts/${receiptId}`, data).then(res => res.data),
  
  deleteReceipt: (submissionId: string, receiptId: string) =>
    API.delete(`/submissions/${submissionId}/receipts/${receiptId}`),
  
  getUploadToken: (submissionId: string, fileName: string, fileSize: number) =>
    API.post(`/submissions/${submissionId}/receipts/upload-token`, { fileName, fileSize }).then(res => res.data),
  
  getViewUrl: (submissionId: string, receiptId: string) =>
    API.get(`/submissions/${submissionId}/receipts/${receiptId}/view-url`).then(res => res.data),
  
  uploadFile: async (
    submissionId: string,
    file: File
  ): Promise<{
    blobName: string;
    originalFileName: string;
    fileSize: number;
    mimeType: string;
  }> => {
    // Step 1: Get SAS token from API
    const tokenResponse = await API.post(
      `/submissions/${submissionId}/receipts/upload-token`,
      {
        fileName: file.name,
        fileSize: file.size,
      }
    ).then(res => res.data);

    // Step 2: Upload directly to Azure Blob Storage
    const uploadResponse = await axios.put(
      tokenResponse.uploadUrl,
      file,
      {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type,
        },
      }
    );

    if (uploadResponse.status !== 201) {
      throw new Error('File upload failed');
    }

    // Step 3: Return the blob details
    return {
      blobName: tokenResponse.blobName,
      originalFileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    };
  },
  
  // Also support new names
  getBySubmission: (submissionId: string) => 
    API.get(`/submissions/${submissionId}/receipts`).then(res => res.data),
  add: (submissionId: string, data: any) =>
    API.post(`/submissions/${submissionId}/receipts`, data).then(res => res.data),
  update: (submissionId: string, receiptId: string, data: any) =>
    API.put(`/submissions/${submissionId}/receipts/${receiptId}`, data).then(res => res.data),
  delete: (submissionId: string, receiptId: string) =>
    API.delete(`/submissions/${submissionId}/receipts/${receiptId}`),
};

// User Management API
export const usersAPI = {
  // Get current user info with real roles from backend
  getCurrentUser: async (): Promise<{ userId: string; email: string; displayName: string; roles: string[] }> => {
    const response = await API.get('/users/me');
    return response.data;
  },

  // Old names for compatibility
  getAllUsers: async (search?: string, filter?: string, pageNumber = 1, pageSize = 50) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (filter) params.append('filter', filter);
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    
    const response = await API.get(`/users?${params.toString()}`);
    return response.data;
  },

  getManagersList: async () => {
    const response = await API.get('/users/managers-list');
    return response.data;
  },

  updateUserManagers: async (userId: string, managerIds: string[]) => {
    const response = await API.put(`/users/${userId}/managers`, { managerIds });
    return response.data;
  },

  getAllForBulkCreate: async (): Promise<UserManagement[]> => {
    const response = await API.get('/users?pageSize=1000');
    return response.data.users;
  },
  
  bulkUpdateManagers: async (userIds: string[], managerIds: string[]) => {
    const response = await API.post('/users/bulk-update-managers', {
      userIds,
      managerIds
    });
    return response.data;
  },

  syncFromAzureAd: async () => {
    const response = await API.post('/users/sync-from-azure-ad');
    return response.data;
  },

  // Also support new names
  getAll: async (params?: { 
    search?: string; 
    filter?: string; 
    page?: number; 
    pageSize?: number;
  }) => {
    const response = await API.get('/users', { params });
    return response.data;
  },

  updateManagers: async (userId: string, managerIds: string[]) => {
    await API.put(`/users/${userId}/managers`, { managerIds });
  },
};

// Reports API
export const reportsAPI = {
  generateSubmissionsReport: async (startDate: string, endDate: string) => {
    try {
      const response = await API.get('/reports/submissions/excel', {
        params: {
          startDate,
          endDate,
        },
        responseType: 'blob', // Important for file downloads
      });

      // Create a download link and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Expense_Report_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }
};

export default API;